import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import { RecordCleaningModal } from '@/components/compliance/RecordCleaningModal';
import {
  ApiError,
  createRun,
  createRunUnavailability,
  deleteRun,
  deleteRunUnavailability,
  getBuildings,
  getRunUnavailability,
  getRuns,
  updateRun,
} from '@/lib/api';
import type {
  Building,
  CreateRunInput,
  CreateUnavailabilityInput,
  Facility,
  Run,
  RunUnavailabilityWindow,
  UpdateRunInput,
} from '@/lib/types';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  Calendar,
  Camera,
  ChevronDown,
  ChevronRight,
  Droplets,
  Edit2,
  FolderOpen,
  Plus,
  ToggleLeft,
  Trash2,
  Wind,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIZE_CATEGORIES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extra_large', label: 'Extra large' },
  { value: 'cat', label: 'Cat' },
] as const;

type SizeCategory = 'small' | 'medium' | 'large' | 'extra_large' | 'cat';

const CAMERA_PROVIDERS = [
  { value: 'hls', label: 'HLS stream' },
  { value: 'mjpeg', label: 'MJPEG' },
  { value: 'reolink', label: 'Reolink' },
  { value: 'unifi', label: 'UniFi' },
  { value: 'rtsp', label: 'RTSP (advanced)' },
  { value: 'other', label: 'Other' },
];

const UNAVAILABILITY_REASONS = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'deep_clean', label: 'Deep clean' },
  { value: 'isolation_required', label: 'Isolation required' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'staff_unavailable', label: 'Staff unavailable' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Run form state
// ---------------------------------------------------------------------------

interface RunFormState {
  // Basics tab
  facilityId: string;
  buildingId: string;
  name: string;
  sizeCategory: SizeCategory;
  speciesAllowed: ('dog' | 'cat')[];
  sortOrder: string;
  // Capacity & physical tab
  capacityMax: string;
  floorAreaSqm: string;
  hasWindow: boolean;
  hasHeating: boolean;
  hasCooling: boolean;
  hasOutdoorAccess: boolean;
  notes: string;
  // Sensors & camera tab
  cameraUrl: string;
  cameraProvider: string;
  sensorsJson: string; // raw JSON text
}

function defaultForm(facilityId: string): RunFormState {
  return {
    facilityId,
    buildingId: '',
    name: '',
    sizeCategory: 'medium',
    speciesAllowed: ['dog'],
    sortOrder: '0',
    capacityMax: '1',
    floorAreaSqm: '',
    hasWindow: false,
    hasHeating: false,
    hasCooling: false,
    hasOutdoorAccess: false,
    notes: '',
    cameraUrl: '',
    cameraProvider: '',
    sensorsJson: '',
  };
}

function formFromRun(run: Run): RunFormState {
  const species = (() => {
    try {
      return JSON.parse(run.speciesAllowed) as ('dog' | 'cat')[];
    } catch {
      return ['dog'] as ('dog' | 'cat')[];
    }
  })();
  return {
    facilityId: run.facilityId,
    buildingId: run.buildingId ?? '',
    name: run.name,
    sizeCategory: run.sizeCategory as SizeCategory,
    speciesAllowed: species,
    sortOrder: String(run.sortOrder),
    capacityMax: String(run.capacityMax ?? 1),
    floorAreaSqm: run.floorAreaSqm != null ? String(run.floorAreaSqm) : '',
    hasWindow: run.hasWindow === 1,
    hasHeating: run.hasHeating === 1,
    hasCooling: run.hasCooling === 1,
    hasOutdoorAccess: run.hasOutdoorAccess === 1,
    notes: run.notes ?? '',
    cameraUrl: run.cameraUrl ?? '',
    cameraProvider: run.cameraProvider ?? '',
    sensorsJson: (() => {
      if (!run.sensorsJson) return '';
      try {
        return JSON.stringify(JSON.parse(run.sensorsJson), null, 2);
      } catch {
        return run.sensorsJson;
      }
    })(),
  };
}

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------

type RunEditorTab = 'basics' | 'capacity' | 'camera' | 'maintenance';

// ---------------------------------------------------------------------------
// Run editor modal (multi-tab)
// ---------------------------------------------------------------------------

interface RunEditorProps {
  run: Run | null; // null = create
  facilities: Facility[];
  buildings: Building[];
  defaultFacilityId: string;
  onClose: () => void;
  onSaved: () => void;
}

function RunEditor({
  run,
  facilities,
  buildings,
  defaultFacilityId,
  onClose,
  onSaved,
}: RunEditorProps) {
  const noun = useSpaceNoun();
  const isCreate = run === null;
  const [form, setForm] = useState<RunFormState>(
    isCreate ? defaultForm(defaultFacilityId) : formFromRun(run),
  );
  const [tab, setTab] = useState<RunEditorTab>('basics');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sensorsError, setSensorsError] = useState<string | null>(null);

  // Unavailability (maintenance tab)
  const [windows, setWindows] = useState<RunUnavailabilityWindow[]>([]);
  const [windowsBusy, setWindowsBusy] = useState(false);
  const [newWindow, setNewWindow] = useState({
    startsAt: '',
    endsAt: '',
    reason: 'maintenance' as CreateUnavailabilityInput['reason'],
    notes: '',
  });
  const [windowError, setWindowError] = useState<string | null>(null);

  // Load existing windows when editing
  useEffect(() => {
    if (!isCreate && run) {
      getRunUnavailability(run.id)
        .then(setWindows)
        .catch(() => {});
    }
  }, [isCreate, run]);

  function toggleSpecies(s: 'dog' | 'cat') {
    setForm((prev) => {
      const has = prev.speciesAllowed.includes(s);
      if (has && prev.speciesAllowed.length === 1) return prev;
      return {
        ...prev,
        speciesAllowed: has
          ? prev.speciesAllowed.filter((x) => x !== s)
          : [...prev.speciesAllowed, s],
      };
    });
  }

  const facilitiesBuildings = buildings.filter(
    (b) => b.facilityId === form.facilityId && b.isActive === 1,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function buildBody(): any {
    let sensorsValue: CreateRunInput['sensorsJson'] = null;
    if (form.sensorsJson.trim()) {
      try {
        sensorsValue = JSON.parse(form.sensorsJson) as CreateRunInput['sensorsJson'];
        setSensorsError(null);
      } catch {
        setSensorsError('sensorsJson is not valid JSON');
        return {};
      }
    }
    return {
      name: form.name.trim(),
      sizeCategory: form.sizeCategory,
      speciesAllowed: form.speciesAllowed,
      sortOrder: Number.isNaN(Number(form.sortOrder)) ? 0 : Number(form.sortOrder),
      buildingId: form.buildingId || null,
      capacityMax: Number.isNaN(Number(form.capacityMax))
        ? 1
        : Math.max(1, Math.min(8, Number(form.capacityMax))),
      floorAreaSqm: form.floorAreaSqm.trim() ? Number(form.floorAreaSqm) : null,
      hasWindow: form.hasWindow ? 1 : 0,
      hasHeating: form.hasHeating ? 1 : 0,
      hasCooling: form.hasCooling ? 1 : 0,
      hasOutdoorAccess: form.hasOutdoorAccess ? 1 : 0,
      notes: form.notes.trim() || null,
      cameraUrl: form.cameraUrl.trim() || null,
      cameraProvider: form.cameraProvider || null,
      sensorsJson: sensorsValue,
    };
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    const body = buildBody();
    if (!body || Object.keys(body).length === 0) {
      setBusy(false);
      return;
    }
    try {
      if (isCreate) {
        await createRun({ ...(body as CreateRunInput), facilityId: form.facilityId });
      } else {
        await updateRun(run.id, body as UpdateRunInput);
      }
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  async function handleAddWindow() {
    if (!run) return;
    if (!newWindow.startsAt || !newWindow.endsAt) {
      setWindowError('Start and end dates are required');
      return;
    }
    if (newWindow.endsAt <= newWindow.startsAt) {
      setWindowError('End must be after start');
      return;
    }
    setWindowsBusy(true);
    setWindowError(null);
    try {
      const w = await createRunUnavailability(run.id, {
        startsAt: newWindow.startsAt,
        endsAt: newWindow.endsAt,
        reason: newWindow.reason,
        notes: newWindow.notes.trim() || null,
      });
      setWindows((prev) => [...prev, w]);
      setNewWindow({ startsAt: '', endsAt: '', reason: 'maintenance', notes: '' });
    } catch (err: unknown) {
      setWindowError(err instanceof ApiError ? err.code : 'Failed to add downtime window');
    } finally {
      setWindowsBusy(false);
    }
  }

  async function handleRemoveWindow(windowId: string) {
    if (!run) return;
    setWindowsBusy(true);
    setWindowError(null);
    try {
      await deleteRunUnavailability(run.id, windowId);
      setWindows((prev) => prev.filter((w) => w.id !== windowId));
    } catch (err: unknown) {
      setWindowError(err instanceof ApiError ? err.code : 'Failed to delete downtime window');
    } finally {
      setWindowsBusy(false);
    }
  }

  const isValid = form.name.trim().length > 0;

  const TABS: { id: RunEditorTab; label: string }[] = [
    { id: 'basics', label: 'Basics' },
    { id: 'capacity', label: 'Capacity & physical' },
    { id: 'camera', label: 'Sensors & camera' },
    { id: 'maintenance', label: 'Maintenance' },
  ];

  return (
    <Modal
      onClose={onClose}
      maxWidth="max-w-2xl"
      aria-label={isCreate ? `Add ${noun.singular}` : `Edit ${noun.singular} — ${run?.name}`}
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 pt-5 pb-4">
          <h2 className="font-heading text-lg font-medium">
            {isCreate ? `Add ${noun.singular}` : `Edit ${noun.singular} — ${run?.name}`}
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border px-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={[
                'px-3 py-2.5 text-sm transition-colors',
                tab === t.id
                  ? 'border-b-2 border-primary font-medium text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          {/* --- Basics tab --- */}
          {tab === 'basics' && (
            <div className="space-y-4">
              {facilities.length > 1 && (
                <div>
                  <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Facility
                  </label>
                  <select
                    value={form.facilityId}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, facilityId: e.target.value, buildingId: '' }))
                    }
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    {facilities.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Building <span className="text-muted-foreground/50">(optional)</span>
                </label>
                <select
                  value={form.buildingId}
                  onChange={(e) => setForm((p) => ({ ...p, buildingId: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">Ungrouped</option>
                  {facilitiesBuildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="run-name"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Name
                </label>
                <input
                  id="run-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Kennel 1"
                  maxLength={50}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Size
                </label>
                <select
                  value={form.sizeCategory}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, sizeCategory: e.target.value as SizeCategory }))
                  }
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {SIZE_CATEGORIES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Species
                </p>
                <div className="flex gap-3">
                  {(['dog', 'cat'] as const).map((s) => (
                    <label key={s} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.speciesAllowed.includes(s)}
                        onChange={() => toggleSpecies(s)}
                        className="rounded border-border"
                      />
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Sort order
                </label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {/* --- Capacity & physical tab --- */}
          {tab === 'capacity' && (
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Max simultaneous pets (1–8)
                </label>
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={form.capacityMax}
                  onChange={(e) => setForm((p) => ({ ...p, capacityMax: e.target.value }))}
                  className="w-32 rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Set above 1 for shared pens that accommodate multiple pets in the same booking.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Floor area (m²) <span className="text-muted-foreground/50">(optional)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={form.floorAreaSqm}
                  onChange={(e) => setForm((p) => ({ ...p, floorAreaSqm: e.target.value }))}
                  placeholder="e.g. 4.5"
                  className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Environment features
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      { key: 'hasWindow', label: 'Window / natural light' },
                      { key: 'hasHeating', label: 'Heating' },
                      { key: 'hasCooling', label: 'Cooling / A/C' },
                      { key: 'hasOutdoorAccess', label: 'Outdoor access / run' },
                    ] as const
                  ).map(({ key, label }) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form[key]}
                        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.checked }))}
                        className="rounded border-border"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Internal notes <span className="text-muted-foreground/50">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="E.g. adjacent to vet room — extra monitoring required"
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {/* --- Sensors & camera tab --- */}
          {tab === 'camera' && (
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Camera stream URL <span className="text-muted-foreground/50">(optional)</span>
                </label>
                <input
                  type="url"
                  value={form.cameraUrl}
                  onChange={(e) => setForm((p) => ({ ...p, cameraUrl: e.target.value }))}
                  placeholder="https://192.168.1.100/stream"
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Camera type
                </label>
                <select
                  value={form.cameraProvider}
                  onChange={(e) => setForm((p) => ({ ...p, cameraProvider: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">None</option>
                  {CAMERA_PROVIDERS.map((cp) => (
                    <option key={cp.value} value={cp.value}>
                      {cp.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Sensors <span className="text-muted-foreground/50">(JSON, optional)</span>
                </label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Array of sensor objects:{' '}
                  <code className="rounded bg-secondary px-1 py-0.5 text-[11px]">
                    {'[{type,endpoint,threshold_min?,threshold_max?}]'}
                  </code>
                  . Valid types: <code className="text-[11px]">temp</code>,{' '}
                  <code className="text-[11px]">humidity</code>,{' '}
                  <code className="text-[11px]">motion</code>,{' '}
                  <code className="text-[11px]">sound</code>.
                </p>
                <textarea
                  rows={5}
                  value={form.sensorsJson}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, sensorsJson: e.target.value }));
                    setSensorsError(null);
                  }}
                  placeholder={
                    '[\n  { "type": "temp", "endpoint": "http://...", "threshold_min": 15, "threshold_max": 25 }\n]'
                  }
                  spellCheck={false}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {sensorsError && <p className="mt-1 text-xs text-destructive">{sensorsError}</p>}
              </div>
            </div>
          )}

          {/* --- Maintenance windows tab --- */}
          {tab === 'maintenance' && (
            <div className="space-y-5">
              {isCreate ? (
                <p className="text-sm text-muted-foreground">
                  Save the run first, then return here to schedule maintenance windows.
                </p>
              ) : (
                <>
                  {windows.length > 0 && (
                    <div className="overflow-x-auto rounded-xl ring-1 ring-border">
                      <table className="min-w-[560px] w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-secondary/40 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                            <th className="px-4 py-2.5 text-left">Starts</th>
                            <th className="px-4 py-2.5 text-left">Ends</th>
                            <th className="px-4 py-2.5 text-left">Reason</th>
                            <th className="px-4 py-2.5 text-left">Notes</th>
                            <th className="px-4 py-2.5" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {windows.map((w) => (
                            <tr key={w.id}>
                              <td className="px-4 py-2.5 font-mono text-xs">{w.startsAt}</td>
                              <td className="px-4 py-2.5 font-mono text-xs">{w.endsAt}</td>
                              <td className="px-4 py-2.5 capitalize text-muted-foreground">
                                {w.reason.replace(/_/g, ' ')}
                              </td>
                              <td className="px-4 py-2.5 text-muted-foreground">
                                {w.notes ?? '—'}
                              </td>
                              <td className="px-4 py-2.5">
                                <button
                                  type="button"
                                  onClick={() => void handleRemoveWindow(w.id)}
                                  className="text-xs text-muted-foreground hover:text-destructive press"
                                  title="Delete window"
                                >
                                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {windows.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No maintenance windows scheduled.
                    </p>
                  )}

                  <div className="rounded-xl border border-border p-4 space-y-3">
                    <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                      Schedule downtime
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-muted-foreground">Start</label>
                        <input
                          type="date"
                          value={newWindow.startsAt}
                          onChange={(e) =>
                            setNewWindow((p) => ({ ...p, startsAt: e.target.value }))
                          }
                          className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-muted-foreground">End</label>
                        <input
                          type="date"
                          value={newWindow.endsAt}
                          onChange={(e) => setNewWindow((p) => ({ ...p, endsAt: e.target.value }))}
                          className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">Reason</label>
                      <select
                        value={newWindow.reason}
                        onChange={(e) =>
                          setNewWindow((p) => ({
                            ...p,
                            reason: e.target.value as CreateUnavailabilityInput['reason'],
                          }))
                        }
                        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        {UNAVAILABILITY_REASONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-muted-foreground">
                        Notes <span className="text-muted-foreground/50">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={newWindow.notes}
                        onChange={(e) => setNewWindow((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="E.g. Annual deep clean"
                        className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    {windowError && <p className="text-xs text-destructive">{windowError}</p>}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        disabled={windowsBusy}
                        onClick={() => void handleAddWindow()}
                        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
                      >
                        <Calendar className="size-3.5" strokeWidth={1.75} />
                        {windowsBusy ? 'Adding…' : 'Add window'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {tab !== 'maintenance' && (
          <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
            {error && <p className="mr-auto self-center text-sm text-destructive">{error}</p>}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!isValid || busy}
              onClick={() => void handleSave()}
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {busy ? 'Saving…' : isCreate ? `Add ${noun.singular}` : 'Save changes'}
            </button>
          </div>
        )}
        {tab === 'maintenance' && (
          <div className="flex justify-end border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-secondary px-4 py-1.5 text-sm font-medium text-foreground press"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Disable confirmation modal
// ---------------------------------------------------------------------------

interface DisableModalProps {
  run: Run;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DisableModal({ run, onCancel, onConfirmed }: DisableModalProps) {
  const noun = useSpaceNoun();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    try {
      await deleteRun(run.id);
      onConfirmed();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label={`Confirm disable ${noun.singular}`}>
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Disable "{run.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This run will be marked inactive and hidden from new booking forms. Existing bookings are
          preserved.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Disabling…' : `Disable ${noun.singular}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal state
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'editor'; run: Run | null }
  | { kind: 'disable'; run: Run }
  // Migration 0100 — record a cleaning / disinfection event for one run.
  | { kind: 'clean'; run: Run };

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Feature icon chips
// ---------------------------------------------------------------------------

function EnvironmentChips({ run }: { run: Run }) {
  const chips: string[] = [];
  if (run.hasWindow) chips.push('Window');
  if (run.hasHeating) chips.push('Heated');
  if (run.hasCooling) chips.push('Cooled');
  if (run.hasOutdoorAccess) chips.push('Outdoor');
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <span
          key={c}
          className="inline-flex items-center rounded-full bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground ring-hairline"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building group section
// ---------------------------------------------------------------------------

interface BuildingGroupProps {
  title: string;
  runs: Run[];
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (run: Run) => void;
  onDisable: (run: Run) => void;
  /** Migration 0100 — record a cleaning event against this run. */
  onRecordCleaning: (run: Run) => void;
  showInactive: boolean;
  buildings: Building[];
}

function BuildingGroup({
  title,
  runs,
  isExpanded,
  onToggle,
  onEdit,
  onDisable,
  onRecordCleaning,
  showInactive,
  buildings: _buildings,
}: BuildingGroupProps) {
  const noun = useSpaceNoun();
  const displayed = showInactive ? runs : runs.filter((r) => r.isActive === 1);
  if (displayed.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 border-b border-border px-6 py-3.5 text-left hover:bg-secondary/30 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-4 text-muted-foreground" strokeWidth={1.75} />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" strokeWidth={1.75} />
        )}
        <FolderOpen className="size-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="font-medium text-sm">{title}</span>
        <span className="ml-auto text-xs text-muted-foreground num">{displayed.length}</span>
      </button>

      {isExpanded && (
        <>
          <div className="grid grid-cols-[1.5fr_1fr_0.75fr_1fr_1fr_auto] gap-4 border-b border-border/50 px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>Name</span>
            <span>Size / species</span>
            <span>Cap.</span>
            <span>Features</span>
            <span>Status</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {displayed.map((run) => {
              const species = (() => {
                try {
                  return (JSON.parse(run.speciesAllowed) as string[]).join(' & ');
                } catch {
                  return run.speciesAllowed;
                }
              })();
              const sizeLabel =
                SIZE_CATEGORIES.find((s) => s.value === run.sizeCategory)?.label ??
                run.sizeCategory;
              return (
                <div
                  key={run.id}
                  className="grid grid-cols-[1.5fr_1fr_0.75fr_1fr_1fr_auto] items-center gap-4 px-6 py-3.5 text-sm"
                >
                  <span className="font-medium">
                    {run.name}
                    {run.cameraUrl && (
                      <Camera
                        className="ml-1.5 inline size-3 text-muted-foreground/60"
                        strokeWidth={1.5}
                      />
                    )}
                  </span>
                  <span className="text-muted-foreground">
                    {sizeLabel} · <span className="capitalize">{species}</span>
                  </span>
                  <span className="text-muted-foreground num">{run.capacityMax ?? 1}</span>
                  <EnvironmentChips run={run} />
                  <StatusPill active={run.isActive === 1} />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(run)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
                      title={`Edit ${noun.singular}`}
                    >
                      <Edit2 className="size-3.5" strokeWidth={1.75} />
                      Edit
                    </button>
                    {run.isActive === 1 && (
                      <button
                        type="button"
                        onClick={() => onRecordCleaning(run)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
                        title="Record cleaning"
                      >
                        <Droplets className="size-3.5" strokeWidth={1.75} />
                        Clean
                      </button>
                    )}
                    {run.isActive === 1 && (
                      <button
                        type="button"
                        onClick={() => onDisable(run)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive press"
                        title={`Disable ${noun.singular}`}
                      >
                        <ToggleLeft className="size-3.5" strokeWidth={1.75} />
                        Disable
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RunsSettings() {
  const { state } = useAuthContext();
  const noun = useSpaceNoun();
  const facilities = state.me?.facilitiesInScope ?? [];
  const defaultFacilityId = state.me?.activeFacility?.id ?? facilities[0]?.id ?? '';

  const [filterFacilityId, setFilterFacilityId] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [runs, setRuns] = useState<Run[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['__ungrouped__']));

  useEffect(() => {
    if (defaultFacilityId && !filterFacilityId) {
      setFilterFacilityId(defaultFacilityId);
    }
  }, [defaultFacilityId, filterFacilityId]);

  const refresh = useCallback(async () => {
    if (!filterFacilityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setPermissionDenied(false);
    try {
      const [runsData, buildingsData] = await Promise.all([
        getRuns(filterFacilityId, true),
        getBuildings(filterFacilityId, true),
      ]);
      setRuns(runsData);
      setBuildings(buildingsData);
      // Auto-expand all building groups on first load
      setExpandedGroups(new Set(['__ungrouped__', ...buildingsData.map((b) => b.id)]));
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
    } finally {
      setLoading(false);
    }
  }, [filterFacilityId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function toggleGroup(id: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Group runs by buildingId
  const groupedRuns = (() => {
    const grouped: { id: string; building: Building | null; runs: Run[] }[] = [];
    const byBuilding = new Map<string, Run[]>();
    const ungrouped: Run[] = [];
    for (const run of runs) {
      if (!run.buildingId) {
        ungrouped.push(run);
      } else {
        const arr = byBuilding.get(run.buildingId) ?? [];
        arr.push(run);
        byBuilding.set(run.buildingId, arr);
      }
    }
    // Buildings in position order
    for (const building of buildings) {
      grouped.push({
        id: building.id,
        building,
        runs: byBuilding.get(building.id) ?? [],
      });
    }
    // Ungrouped last
    if (ungrouped.length > 0) {
      grouped.push({ id: '__ungrouped__', building: null, runs: ungrouped });
    }
    return grouped;
  })();

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage runs
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-end justify-between gap-4">
        <h1 className="font-heading text-2xl font-medium tracking-tight">{noun.Plural}</h1>
        <button
          type="button"
          onClick={() => setModal({ kind: 'editor', run: null })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add {noun.singular}
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {facilities.length > 1 && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="filter-facility"
              className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Facility
            </label>
            <select
              id="filter-facility"
              value={filterFacilityId}
              onChange={(e) => setFilterFacilityId(e.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-border"
          />
          Show inactive
        </label>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : runs.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Wind className="mx-auto mb-3 size-8 text-muted-foreground/40" strokeWidth={1.25} />
          <p className="text-sm font-medium text-foreground">No {noun.plural} configured yet.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add individual kennels, pens, or cat units.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'editor', run: null })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            Add {noun.singular}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedRuns.map(({ id, building, runs: groupRuns }) => (
            <BuildingGroup
              key={id}
              title={building ? building.name : 'Ungrouped'}
              runs={groupRuns}
              isExpanded={expandedGroups.has(id)}
              onToggle={() => toggleGroup(id)}
              onEdit={(r) => setModal({ kind: 'editor', run: r })}
              onDisable={(r) => setModal({ kind: 'disable', run: r })}
              onRecordCleaning={(r) => setModal({ kind: 'clean', run: r })}
              showInactive={showInactive}
              buildings={buildings}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {modal.kind === 'editor' && (
        <RunEditor
          run={modal.run}
          facilities={facilities}
          buildings={buildings}
          defaultFacilityId={filterFacilityId || defaultFacilityId}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
      {modal.kind === 'disable' && (
        <DisableModal
          run={modal.run}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
      {modal.kind === 'clean' && (
        <RecordCleaningModal
          facilityId={modal.run.facilityId}
          runs={[{ id: modal.run.id, name: modal.run.name }]}
          initialRunId={modal.run.id}
          onClose={() => setModal({ kind: 'none' })}
        />
      )}
    </div>
  );
}
